import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LeadSourcePageComponent } from './page/lead-source-page/lead-source-page.component';
import { NotFoundPageComponent } from './page/not-found-page/not-found-page.component';
import { MainLayoutComponent } from './layouts/main-layout.component';

import { PartnerSellerPageComponent } from './page/partner-seller-page/partner-seller-page.component';
import { InvoicesListPageComponent } from './page/invoices-list-page/invoices-list-page.component';
import { LightEntrepreneurClientPageComponent } from './page/light-entrepreneur-client-page/light-entrepreneur-client-page.component';
import { PartnersListPageComponent } from './page/partners-list-page/partners-list-page.component';
import { PartnerPageComponent } from './page/partner-page/partner-page.component';
import { ClientPageComponent } from './page/client-page/client-page.component';
import { InvoicePageComponent } from './page/invoice-page/invoice-page.component';
import { ClientsPageComponent } from './page/clients-page/clients-page.component';
import { ClientRepresentativePageComponent } from './page/client-representative-page/client-representative-page.component';
import { SettingsPageComponent } from './page/settings-page/settings-page.component';
import { TemplatesListPageComponent } from './page/templates-list-page/templates-list-page.component';
import { EmailTemplatePageComponent } from './page/email-template-page/email-template-page.component';
import { SmsTemplatePageComponent } from './page/sms-template-page/sms-template-page.component';
import { LeadSourcesListPageComponent } from './page/lead-sources-list-page/lead-sources-list-page.component';
import { DashboardPageComponent } from './page/dashboard-page/dashboard-page.component';
import { InvoiceCreatePageComponent } from './page/invoice-create-page/invoice-create-page.component';
import { LeadInformationsListPageComponent } from './page/lead-informations-list-page/lead-informations-list-page.component';
import { PaymentHistoriesListPageComponent } from './page/payment-histories-list-page/payment-histories-list-page.component';
import { PaymentHistoryImportPageComponent } from './page/payment-history-import-page/payment-history-import-page.component';
import { ReturnUrlRedirectComponent } from './components/return-url-redirect/return-url-redirect.component';
import { OutgoingPaymentsListPageComponent } from './page/outgoing-payments-list-page/outgoing-payments-list-page.component';
import { IncomingPaymentsListPageComponent } from './page/incoming-payments-list-page/incoming-payments-list-page.component';
import { PcrTasksListPageComponent } from './page/pcr-tasks-list-page/pcr-tasks-list-page.component';
import { OperationalTasksListPageComponent } from './page/operational-tasks-list-page/operational-tasks-list-page.component';
import { EndCustomerDuplicatesPageComponent } from './page/end-customer-duplicates-page/end-customer-duplicates-page.component';
import { EndCustomerMergePageComponent } from './page/end-customer-merge-page/end-customer-merge-page.component';
import { PermissionGuard } from './page/permission-guard';
import { InvoiceSubPageRollbackGuard } from './page/invoice-sub-page-rollback.guard';
import { StranglerGuard } from './page/strangler-guard';
import { Permission } from './api/models/permission';

// console-svelte's src/lib/nav/nav-parity.spec.ts reads the paths declared
// here, to check that MainNavComponent's svelteOnly entries are exactly those
// with no route.
export const routes: Routes = [
  {
    path: '', component: MainLayoutComponent,
    children: [
      // Dashboard
      {
        path: '', component: DashboardPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '' }]
        }
      },
      // Clients
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'clients', component: ClientsPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state. The URL itself is intentionally not migrated —
        // the create flow is unreachable in console-svelte — so only the
        // rolled-back state renders anything here.
        path: 'clients/new', component: ClientPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, { label: 'Client' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'clients/:clientId', component: ClientPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, { label: 'Client' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'clients/:clientId/representatives',
        component: ClientRepresentativePageComponent,
        canActivate: [StranglerGuard],
        data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, {
            label: 'Client',
            route: '/clients/:clientId'
          }, { label: 'Representative' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'clients/:clientId/representatives/:clientRepresentativeId',
        component: ClientRepresentativePageComponent,
        canActivate: [StranglerGuard],
        data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, {
            label: 'Client',
            route: '/clients/:clientId'
          }, { label: 'Representative' }]
        }
      },
      // Light Entrepreneur Clients
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state. The URL itself is intentionally not migrated —
        // console-svelte has no light-entrepreneur create flow — so only the
        // rolled-back state renders anything here.
        path: 'light-entrepreneur-clients/new', component: LightEntrepreneurClientPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, { label: 'New Light Entrepreneur Client' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state. The URL now only forwards to /clients/{id},
        // which renders light-entrepreneur clients too; the guard is what
        // turns the invoice page's light-entrepreneur widget routerLink into
        // the document load that reaches that forward.
        path: 'light-entrepreneur-clients/:clientId', component: LightEntrepreneurClientPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Clients', route: '/clients' }, { label: 'Light Entrepreneur Client' }]
        }
      },
      // End Customers
      {
        path: 'end-customers/duplicates', component: EndCustomerDuplicatesPageComponent,
        canActivate: [PermissionGuard],
        data: {
          permission: Permission.EndCustomersMerge,
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'End Customer Duplicates' }]
        }
      },
      {
        path: 'end-customers/duplicates/:businessId', component: EndCustomerMergePageComponent,
        canActivate: [PermissionGuard],
        data: {
          permission: Permission.EndCustomersMerge,
          breadcrumb: [
            { label: 'Dashboard', route: '/' },
            { label: 'End Customer Duplicates', route: '/end-customers/duplicates' },
            { label: 'Merge' }
          ]
        }
      },
      // Invoices
      {
        path: 'invoices', component: InvoicesListPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Company Invoices' }],
          clientType: 'COMPANY',
          title: 'Company Invoices',
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state. Declared before 'invoices/:invoiceId' so 'new'
        // is never matched as an invoice id.
        path: 'invoices/new', component: InvoiceCreatePageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Company Invoices', route: '/invoices' }, { label: 'Invoice' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads. Unlike the other migrated routes
        // this URL has NO Angular twin — the Invoice Activity page was born
        // in the Svelte bundle — so the rolled-back state has nothing to
        // render and InvoiceSubPageRollbackGuard redirects to the invoice
        // page instead (add-invoice-activity-page design D5). Declared before
        // 'invoices/:invoiceId' so the sub-path is matched here, not as an
        // invoice id.
        path: 'invoices/:invoiceId/activity',
        canActivate: [StranglerGuard, InvoiceSubPageRollbackGuard],
        children: []
      },
      {
        // The Invoice Documents page, the same shape as Activity above: born
        // in the Svelte bundle, so a rollback redirects to the invoice page.
        // No PermissionGuard: the Svelte page renders its own no-access state.
        path: 'invoices/:invoiceId/documents',
        canActivate: [StranglerGuard, InvoiceSubPageRollbackGuard],
        children: []
      },
      {
        path: 'invoices/:invoiceId', component: InvoicePageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Invoices' }, { label: 'Invoice' }]
        }
      },
      {
        path: 'light-entrepreneur-invoices', component: InvoicesListPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Light Entrepreneur Invoices' }],
          clientType: 'LIGHT_ENTREPRENEUR',
          title: 'Light Entrepreneur Invoices',
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'outgoing-payments', component: OutgoingPaymentsListPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Outgoing Payments' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'pcr-tasks', component: PcrTasksListPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'PCR Tasks' }]
        }
      },
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'operational-tasks', component: OperationalTasksListPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Operational Tasks' }]
        }
      },
      // LeadInformations
      {
        // Migrated to console-svelte (strangler): the guard turns in-app
        // navigations into document loads; the component still serves the
        // rolled-back state.
        path: 'leads', component: LeadInformationsListPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Leads' }]
        }
      },
      // Payment Histories
      {
        path: 'payment-histories', component: PaymentHistoriesListPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'End Customer Payments' }]
        }
      },
      {
        path: 'incoming-payments', component: IncomingPaymentsListPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'Incoming Payments' }]
        }
      },
      {
        path: 'payment-histories/import', component: PaymentHistoryImportPageComponent, data: {
          breadcrumb: [{ label: 'Dashboard', route: '/' }, { label: 'End Customer Payments', route: '/payment-histories' }, { label: 'Import' }]
        }
      },
      // Settings
      {
        path: 'settings', component: SettingsPageComponent, data: {
          breadcrumb: [{ label: 'Settings' }]
        }
      },
      // Settings / Templates
      {
        path: 'settings/templates', component: TemplatesListPageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Templates' }
          ]
        }
      },
      {
        path: 'settings/templates/email/new', component: EmailTemplatePageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Templates', route: '/settings/templates' },
            { label: 'Email Template' }
          ]
        }
      },
      {
        path: 'settings/templates/email/:templateId', component: EmailTemplatePageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Templates', route: '/settings/templates' },
            { label: 'Email Template' }
          ]
        }
      },
      {
        path: 'settings/templates/sms/new', component: SmsTemplatePageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Templates', route: '/settings/templates' },
            { label: 'Text Message Template' }
          ]
        }
      },
      {
        path: 'settings/templates/sms/:templateId', component: SmsTemplatePageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Templates', route: '/settings/templates' },
            { label: 'Text Message Template' }
          ]
        }
      },
      // Settings / Lead Sources — migrated to console-svelte (strangler):
      // the guard turns in-app navigations into document loads; the
      // components still serve the rolled-back state.
      {
        path: 'settings/lead-sources', component: LeadSourcesListPageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Lead Sources' }
          ]
        }
      },
      {
        path: 'settings/lead-sources/new', component: LeadSourcePageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Lead Sources', route: '/settings/lead-sources' },
            { label: 'Lead Source' }
          ]
        }
      },
      {
        path: 'settings/lead-sources/:leadSourceId', component: LeadSourcePageComponent,
        canActivate: [StranglerGuard], data: {
          breadcrumb: [
            { label: 'Settings', route: 'settings' },
            { label: 'Lead Sources', route: '/settings/lead-sources' },
            { label: 'Lead Source' }
          ]
        }
      },
      // Settings / Partners
      {
        path: 'settings/partners', component: PartnersListPageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Partners' }
          ]
        }
      },
      {
        path: 'settings/partners/new', component: PartnerPageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Partners', route: '/settings/partners' },
            { label: 'Partner' }
          ]
        }
      },
      {
        path: 'settings/partners/:partnerId', component: PartnerPageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Partners', route: '/settings/partners' },
            { label: 'Partner' }
          ]
        }
      },
      {
        path: 'settings/partners/:partnerId/sellers/new', component: PartnerSellerPageComponent, data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Partners', route: '/settings/partners' },
            { label: 'Partner', route: '/settings/partners/:partnerId' },
            { label: 'Seller' }
          ]
        }
      },
      {
        path: 'settings/partners/:partnerId/sellers/:partnerSellerId',
        component: PartnerSellerPageComponent,
        data: {
          breadcrumb: [
            { label: 'Settings', route: '/settings' },
            { label: 'Partners', route: '/settings/partners' },
            { label: 'Partner', route: '/settings/partners/:partnerId' },
            { label: 'Seller' }
          ]
        }
      },
    ]
  },
  // Login
  //{path: 'login', component: LoginPageComponent},
  //{ path: 'login', component: LoginComponent },

  // Login path is handled by the proxy
  { path: 'auth/login', component: ReturnUrlRedirectComponent },

  { path: 'index.html', redirectTo: '/' },

  // Not Found
  { path: '**', component: NotFoundPageComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule]
})
export class AppRoutingModule { }
